/**
 * @file BlendPriceImportModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-17
 * @modified 2026-09-04
 * @description Import raw material prices from a supplier's price list PDF.
 *
 *              Replaces the monthly ritual of reading the supplier's PDF, adding
 *              £17 to every figure by hand, and typing ~120 numbers into a grid.
 *
 *              The uplift is shown and editable rather than applied invisibly.
 *              Every cell shows what the supplier printed, what is being added,
 *              and what the price is now, because a price that silently gained
 *              £17 twice would be impossible to spot afterwards and would feed
 *              straight into every blend quote.
 *
 *              Nothing is saved until Apply. Names the reader couldn't place are
 *              listed for mapping rather than dropped or guessed at, and mapping
 *              one can teach the alias so next month's list matches on its own.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import DateInput from './DateInput';
import TickCheckbox from './TickCheckbox';
import SearchableDropdown from './SearchableDropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { formatDate } from '../utils/dateFormatters';
import './BlendCalculator.css';

const today = () => new Date().toISOString().slice(0, 10);
const DEFAULT_UPLIFT = '17';

const CONFIDENCE = {
  high: { icon: 'check_circle', text: 'Read cleanly' },
  medium: { icon: 'info', text: 'Check the figures before applying' },
  low: { icon: 'warning', text: 'Low confidence — check every figure' }
};

/** The three roles, in the order a price list prints them. */
const ROLES = ['spot', 'current', 'upcoming'];
const ROLE_LABEL = { spot: 'SPOT', current: 'CURRENT', upcoming: 'UPCOMING' };

/** Compare printed headings to column names without punctuation getting in the
 *  way: "Oct 26 - Apr 27" and "Oct 26 – Apr 27" are the same thing. */
const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const num = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** Two decimals, but only when they say something. 236 not 236.00. */
const money = (v) => (v === null || v === undefined ? '—' : String(Number(v)));

function ImportBody({
  stage, error, result, rows, setRows, unmatched, setUnmatched, materialOptions,
  uplift, setUplift, effectiveDate, setEffectiveDate, remember, setRemember,
  columns, before, onPickFile, onDrop, applyCount,
  activeRoles, mapping, setMapping, headingFor, mappingProblem, destinations, destByKey
}) {
  useSlideOutButton([stage, applyCount]);

  if (stage !== 'review') {
    return (
      <div className="fm-shot-body">
        {error && <div className="formulation-error">{error}</div>}
        <div
          className={`fm-shot-drop${stage === 'reading' ? ' busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('over'); }}
          onDragLeave={(e) => e.currentTarget.classList.remove('over')}
          onDrop={(e) => { e.currentTarget.classList.remove('over'); onDrop(e); }}
          onClick={stage === 'reading' ? undefined : onPickFile}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPickFile(); }}
        >
          {stage === 'reading' ? (
            <>
              <span className="material-symbols-rounded spinning">progress_activity</span>
              <span className="fm-shot-drop-title">Reading the price list…</span>
              <span className="fm-shot-drop-hint">This usually takes ten seconds or so.</span>
            </>
          ) : (
            <>
              <span className="material-symbols-rounded">upload_file</span>
              <span className="fm-shot-drop-title">
                Drop the supplier's price list here, or click to choose it
              </span>
              <span className="fm-shot-drop-hint">
                PDF, up to 20 MB. Prices are read exactly as printed — the uplift is
                added afterwards, where you can see it.
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  const conf = CONFIDENCE[result.confidence] || CONFIDENCE.medium;

  return (
    <div className="blend-import-body">
      {error && <div className="formulation-error">{error}</div>}

      <div className={`fm-shot-verdict conf-${result.confidence}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span className="material-symbols-rounded">{conf.icon}</span>
          <strong>{conf.text}</strong>
        </div>
        <div className="fm-shot-verdict-note">
          {rows.length} material{rows.length === 1 ? '' : 's'} read
          {result.column_headings?.length
            ? ` · columns read as ${result.column_headings.join(' / ')}`
            : ''}
          {result.notes ? ` · ${result.notes}` : ''}
        </div>
      </div>

      <div className="blend-import-controls">
        <div className="filter-group">
          <label htmlFor="bi-uplift">Uplift £/T</label>
          <Input id="bi-uplift" type="number" inputMode="decimal" value={uplift}
                 onChange={(e) => setUplift(e.target.value)} />
          <span className="blend-field-hint">Added to every price read from the PDF.</span>
        </div>
        <div className="filter-group">
          <label htmlFor="bi-date">Effective from</label>
          <DateInput id="bi-date" value={effectiveDate}
                     onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>
      </div>

      {/* Where each column on the PDF actually goes.
          The reader fills its three slots by POSITION — leftmost column first —
          because it cannot know which contract period a heading like "NEXT"
          refers to. This is where that is decided, and it has to be decided by
          a person: a list headed SPOT and NEXT would otherwise land its second
          column on the current season and quietly overwrite it. */}
      <div className="blend-import-mapping">
        <div className="blend-import-mapping-head">
          <span className="material-symbols-rounded">swap_horiz</span>
          <div>
            <strong>Where each column goes</strong>
            <span className="blend-field-hint">
              Read left to right off the PDF. Check each one lands on the right period
              before applying — the headings below are as printed.
            </span>
          </div>
        </div>

        <div className="blend-import-mapping-rows">
          {activeRoles.map((role, i) => {
            const printed = result?.column_headings?.[i];
            const chosen = mapping[role] || [];
            // A heading that names its period — "Oct 26 - Apr 27" — confirms the
            // mapping on its own. One that does not, like "NEXT", is a guess we
            // cannot make: the PDF never says what NEXT means. Say so rather
            // than let it look settled.
            const unconfirmed = chosen.length === 1 && printed && norm(printed)
              && norm(destByKey.get(chosen[0])?.label) !== norm(printed);
            return (
              <div key={role} className="blend-import-mapping-row">
                <span className="blend-import-mapping-from">
                  <span className="blend-import-colno">Column {i + 1}</span>
                  <strong>{headingFor(role, i)}</strong>
                </span>
                <span className="material-symbols-rounded blend-import-arrow">arrow_forward</span>
                <div className="blend-import-mapping-to">
                  <MultiSelectDropdown
                    value={chosen}
                    onChange={(v) => setMapping({ ...mapping, [role]: v })}
                    selectedInMenu={true}
                    options={destinations.map((d) => ({
                      value: d.key,
                      label: (
                        <span className="blend-dest-option">
                          <span>{d.label}</span>
                          {d.note && <em className="blend-dest-note">{d.note}</em>}
                        </span>
                      ),
                      searchText: d.label
                    }))}
                    placeholder={chosen.length ? `${chosen.length} selected` : 'Not imported'}
                  />
                  {/* One column of a price list can legitimately answer for more
                      than one of ours, so the chosen periods are listed rather
                      than left as a count. */}
                  {chosen.length > 0 && (
                    <span className="blend-dest-chips">
                      {chosen.map((k) => {
                        const d = destByKey.get(k);
                        if (!d) return null;
                        return (
                          <span key={k} className={`blend-dest-chip${d.columnId ? '' : ' is-new'}`}>
                            {d.label}
                            {!d.columnId && <em>new</em>}
                          </span>
                        );
                      })}
                    </span>
                  )}
                  {unconfirmed && (
                    <span className="blend-field-hint warn">
                      “{printed}” doesn’t say which period it is — this is a guess. Check it.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {mappingProblem && (
          <div className="blend-import-mapping-problem">
            <span className="material-symbols-rounded">error</span>
            <span>{mappingProblem}</span>
          </div>
        )}
      </div>

      <div className="table-container blend-import-table-wrap">
        <table className="data-table blend-import-table">
          <thead>
            <tr>
              <th className="fm-shot-use">Use</th>
              <th>Material</th>
              {/* Headed by the DESTINATION, not by our internal slot name.
                  A column headed UPCOMING that is going into "Oct 26 – Apr 27"
                  is only confusing; what matters when checking figures is which
                  period they are about to land on. */}
              {activeRoles.map((role, i) => (
                <th key={role} className="num">
                  {(mapping[role] || []).length
                    ? (mapping[role] || []).map((k) => destByKey.get(k)?.label).filter(Boolean).join(' + ')
                    : <span className="muted">not imported</span>}
                  <em className="blend-import-th-from">{headingFor(role, i)}</em>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.material_key} className={row.use ? '' : 'skipped'}>
                <td className="fm-shot-use">
                  <TickCheckbox
                    id={`bi-${row.material_key}`}
                    checked={row.use}
                    ariaLabel={`Apply ${row.material_name}`}
                    onChange={() => setRows(rows.map((r, j) =>
                      j === i ? { ...r, use: !r.use } : r))}
                  />
                </td>
                <td>
                  <span className="blend-material-name">{row.material_name}</span>
                  {row.printed_name
                    && row.printed_name.toLowerCase() !== row.material_name.toLowerCase() && (
                    <span className="blend-material-meta">printed as “{row.printed_name}”</span>
                  )}
                </td>
                {activeRoles.map((role) => {
                  const printed = row[role];
                  if (printed === null) {
                    return <td key={role} className="num muted">not quoted</td>;
                  }
                  const final = num(row.final[role]);
                  // The prior price is the one in the column this is going TO,
                  // so "was 236" answers the question actually being asked.
                  // Only shown when there is ONE destination: against two, "was"
                  // has two different answers and picking either would mislead.
                  const dests = mapping[role] || [];
                  const soleColumnId = dests.length === 1
                    ? destByKey.get(dests[0])?.columnId : null;
                  const prior = soleColumnId
                    ? before.get(`${row.material_key}:${soleColumnId}`)
                    : undefined;
                  return (
                    <td key={role} className="num">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="blend-cell-input"
                        value={row.final[role]}
                        onChange={(e) => setRows(rows.map((r, j) =>
                          j === i ? { ...r, final: { ...r.final, [role]: e.target.value } } : r))}
                        aria-label={`${ROLE_LABEL[role]} for ${row.material_name}`}
                      />
                      {/* The derivation, so a double-applied uplift is obvious. */}
                      <span className="blend-import-derived">
                        {money(printed)} + {num(uplift) ?? 0}
                        {prior !== undefined && prior !== null && (
                          <> · was {money(prior)}
                            {final !== null && Number(prior) !== final && (
                              <em className={final > Number(prior) ? 'up' : 'down'}>
                                {final > Number(prior) ? '▲' : '▼'}
                                {Math.abs(final - Number(prior)).toFixed(2)}
                              </em>
                            )}
                          </>
                        )}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {unmatched.length > 0 && (
        <div className="fm-shot-unmatched">
          <span className="material-symbols-rounded">help</span>
          <div>
            <strong>Not recognised</strong>
            <span className="fm-shot-unmatched-hint">
              Pick the material each belongs to, or leave it and its prices are skipped.
            </span>
            <table className="data-table blend-import-unmatched">
              <thead>
                <tr>
                  <th>On the list</th>
                  <th>Is our material</th>
                  {activeRoles.map((role, i) => (
                    <th key={role} className="num">{headingFor(role, i)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {unmatched.map((u, i) => (
                  <tr key={i} className={u.materialKey ? 'mapped' : 'unmatched'}>
                    <td>{u.printed_name}</td>
                    <td>
                      <SearchableDropdown
                        options={materialOptions}
                        value={u.materialKey}
                        onChange={(v) => setUnmatched(unmatched.map((x, j) =>
                          j === i ? { ...x, materialKey: v } : x))}
                        placeholder="Pick a material…"
                        searchPlaceholder="Name, code or alias…"
                      />
                    </td>
                    {activeRoles.map((r) => (
                      <td key={r} className="num">{money(u[r])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {unmatched.some((u) => u.materialKey) && (
              <label className="blend-paste-remember">
                <TickCheckbox
                  id="bi-remember"
                  checked={remember}
                  onChange={() => setRemember(!remember)}
                  ariaLabel="Remember these spellings"
                />
                <span>
                  Remember these spellings, so next month's list matches them on its own.
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      <p className="fm-shot-summary">
        {applyCount} price{applyCount === 1 ? '' : 's'} will be written to{' '}
        {[...new Set(activeRoles.flatMap((role) => mapping[role] || []))]
          .map((k) => destByKey.get(k)?.label)
          .filter(Boolean)
          .join(', ') || 'nowhere yet'}
        , effective {formatDate(effectiveDate)}. Nothing changes until you apply.
      </p>
    </div>
  );
}

export default function BlendPriceImportModal({ book, onClose, onDone }) {
  const [stage, setStage] = useState('pick');       // pick | reading | review
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [uplift, setUplift] = useState(DEFAULT_UPLIFT);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [remember, setRemember] = useState(true);
  const [applying, setApplying] = useState(false);
  // Which destinations each of the reader's three positional slots writes to.
  // A LIST per slot, because one column on a price list legitimately answers
  // for more than one of ours — a September spot price is also the Summer 26
  // price when September is the last month of Summer 26, and typing it twice is
  // how the two drift apart.
  const [mapping, setMapping] = useState({ spot: [], current: [], upcoming: [] });
  // The four periods an import may target: the one we are in, the one before,
  // and the next two. Fetched rather than derived from the columns, because the
  // point is to offer periods that have NO column yet.
  const [periodWindow, setPeriodWindow] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/blends/book/period-window`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (alive && d?.window) setPeriodWindow(d.window); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const columns = useMemo(() => book?.columns || [], [book]);
  const materials = useMemo(() => book?.materials || [], [book]);

  const materialOptions = useMemo(() => materials.map((m) => ({
    value: m.key,
    label: m.name,
    badge: m.supplier_code || undefined,
    searchText: `${m.name} ${m.supplier_code || ''} ${(m.aliases || []).join(' ')}`
  })), [materials]);

  /**
   * What each material costs today in each price COLUMN, so a change reads as a
   * change. Keyed by column rather than by role because the mapping is now the
   * user's to set: re-point a slot at a different period and the "was" figure
   * has to follow it, or it would compare against a column nothing is being
   * written to.
   */
  const before = useMemo(() => {
    const byKey = new Map(materials.map((m) => [m.id, m.key]));
    const map = new Map();
    for (const row of book?.costs || []) {
      const key = byKey.get(row.material_id);
      if (key) map.set(`${key}:${row.price_column_id}`, row.cost_per_tonne);
    }
    return map;
  }, [book, materials]);

  /**
   * Re-derive every final price when the uplift changes.
   *
   * Deliberately overwrites hand edits: the uplift is a global correction, and
   * leaving some cells on the old figure would be a silent inconsistency in
   * exactly the data nobody can check by eye.
   */
  useEffect(() => {
    const u = num(uplift) ?? 0;
    setRows((prev) => prev.map((r) => ({
      ...r,
      final: Object.fromEntries(ROLES.map((role) =>
        [role, r[role] === null ? '' : String(Number((r[role] + u).toFixed(2)))]))
    })));
  }, [uplift]);

  const send = useCallback(async (file) => {
    if (!file) return;
    setError('');
    setStage('reading');
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${API_URL}/api/blends/price-list/extract`,
        { method: 'POST', credentials: 'include', body });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not read that PDF');
      if (!data.prices?.length && !data.unmatched?.length) {
        setError('No prices were found in that PDF. Check it is a raw material price list.');
        setStage('pick');
        return;
      }

      const u = num(uplift) ?? 0;
      setResult(data);
      setRows((data.prices || []).map((r) => ({
        ...r,
        use: true,
        final: Object.fromEntries(ROLES.map((role) =>
          [role, r[role] === null ? '' : String(Number((r[role] + u).toFixed(2)))]))
      })));
      setUnmatched((data.unmatched || []).map((x) => ({ ...x, materialKey: null })));
      setStage('review');
    } catch (e) {
      setError(e.message);
      setStage('pick');
    }
    // uplift is read once at load; the effect above re-derives on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const file = [...(e.dataTransfer?.files || [])].find((f) => f.type === 'application/pdf');
    if (file) send(file);
    else setError('That file is not a PDF.');
  }, [send]);

  /**
   * Everywhere a column of prices may be sent.
   *
   * Spot first — it is not a contract period and could not be offered as one —
   * then the four periods in the window. A period with no price column yet is
   * still offered; choosing it creates the column, and for a period the
   * calendar has not reached, the period too. That is the whole point: the book
   * had no Winter 2025-26 column at all, so a list quoting it had nowhere to go
   * and the prices went somewhere else.
   */
  const destinations = useMemo(() => {
    const out = columns
      .filter((c) => !c.period_id)
      .map((c) => ({ key: `col:${c.id}`, label: c.label, columnId: c.id }));

    for (const w of periodWindow) {
      out.push({
        key: w.period_id ? `per:${w.period_id}` : `new:${w.name}`,
        label: w.name,
        slot: w.slot,
        columnId: w.price_column_id || null,
        periodId: w.period_id || null,
        name: w.name,
        startDate: w.start_date,
        endDate: w.end_date,
        // Said on the option itself. Creating a column is harmless but it is
        // still a change to the cost book, and nobody should discover it after.
        note: w.price_column_id ? null
          : w.period_id ? 'price column will be created'
            : 'period and price column will be created'
      });
    }
    return out;
  }, [columns, periodWindow]);

  const destByKey = useMemo(
    () => new Map(destinations.map((d) => [d.key, d])), [destinations]);

  /**
   * The slots that actually carry figures.
   *
   * The reader fills its three slots by position and leaves the rest null, so a
   * two-column price list arrives with `upcoming` empty throughout. Showing a
   * mapping control and a table column for a slot with nothing in it invites
   * somebody to point it at a period and wonder why no prices moved.
   */
  const activeRoles = useMemo(() => {
    const carries = (role) => rows.some((r) => r[role] !== null && r[role] !== undefined)
      || unmatched.some((u) => u[role] !== null && u[role] !== undefined);
    return ROLES.filter(carries);
  }, [rows, unmatched]);

  /**
   * What the PDF called this column. The reader reports one heading per column
   * present, in printed order, so slot i is heading i. Falls back to our own
   * slot name when a heading is missing rather than showing nothing — the user
   * still has to be able to tell the columns apart to map them.
   */
  const headingFor = useCallback((role, i) => {
    const printed = result?.column_headings?.[i];
    return printed && String(printed).trim() ? printed : ROLE_LABEL[role];
  }, [result]);

  /**
   * Seed the mapping when a file is read, in two passes.
   *
   * First, any column whose PRINTED heading is the same text as a destination's
   * name — "Oct 26 - Apr 27" against the Winter 2026-27 column labelled that —
   * normalised so a hyphen, an en dash and a stray space do not defeat it. That
   * is a string match on what the supplier actually wrote, not a guess at what
   * they meant.
   *
   * Then position fills what is left: first slot to Spot, second to the period
   * we are in, third to the next one. A destination already claimed is never
   * handed out again, so a two-column list does not default both of its columns
   * onto the same period.
   *
   * A starting point in every case. Nothing here decides anything — the user
   * sees each mapping, can add destinations to it, and can clear it.
   */
  useEffect(() => {
    if (stage !== 'review' || !destinations.length) return;
    const headings = result?.column_headings || [];
    const taken = new Set();
    const next = { spot: [], current: [], upcoming: [] };

    const claim = (role, dest) => {
      if (!dest || taken.has(dest.key)) return false;
      next[role] = [dest.key];
      taken.add(dest.key);
      return true;
    };

    ROLES.forEach((role, i) => {
      const h = norm(headings[i]);
      if (!h) return;
      // The period's own name, and the label of the column already standing for
      // it — a supplier writing "Oct 26 - Apr 27" matches the column, not the
      // period called "Winter 2026-27".
      claim(role, destinations.find((d) => !taken.has(d.key) && (
        norm(d.label) === h
        || norm(columns.find((c) => c.id === d.columnId)?.label) === h)));
    });

    const bySlot = (slot) => destinations.find((d) => d.slot === slot);
    const positional = [
      destinations.find((d) => !d.slot),        // Spot
      bySlot('current'),
      bySlot('next')
    ];
    ROLES.forEach((role, i) => {
      if (next[role].length) return;
      claim(role, positional[i]);
    });

    setMapping(next);
  }, [stage, result, destinations, columns]);

  /**
   * Two columns from the SAME price list cannot both write to one period.
   * Whichever was applied last would win, silently, and the figures on screen
   * would not be the figures saved.
   *
   * One column writing to SEVERAL periods is fine, and is the reason this is a
   * list — a September spot price is also that month's Summer 26 price.
   */
  const mappingProblem = useMemo(() => {
    const owner = new Map();
    for (const role of activeRoles) {
      for (const key of mapping[role] || []) {
        if (owner.has(key) && owner.get(key) !== role) {
          const label = destByKey.get(key)?.label || 'the same period';
          return `Two of the columns read from this PDF are both set to write to “${label}”. `
            + 'Remove it from one of them.';
        }
        owner.set(key, role);
      }
    }
    if (activeRoles.length && !owner.size) {
      return 'No column is set to import anywhere. Choose where at least one of them goes.';
    }
    return null;
  }, [activeRoles, mapping, destByKey]);

  /**
   * How many prices will be written: every ticked figure, once per destination
   * it is going to. Counted rather than built here because the destinations are
   * not all resolvable yet — a period with no column gets one at Apply, and
   * asking the server for it on every keystroke would create columns for
   * choices the user then changed their mind about.
   */
  const applyCount = useMemo(() => {
    let n = 0;
    const fan = (role) => (mapping[role] || []).length;
    for (const r of rows) {
      if (!r.use) continue;
      for (const role of activeRoles) {
        if (r[role] === null) continue;
        n += fan(role);
      }
    }
    for (const x of unmatched) {
      if (!x.materialKey) continue;
      for (const role of activeRoles) {
        if (x[role] === null) continue;
        n += fan(role);
      }
    }
    return n;
  }, [rows, unmatched, mapping, activeRoles]);

  /**
   * Turn the chosen destinations into real price column ids, creating a column
   * — and, for a period the calendar has not reached, the period — where one
   * does not exist yet.
   *
   * Done at Apply and only for destinations actually in use, so nothing is
   * created for a choice that was made and then undone.
   */
  const resolveDestinations = useCallback(async () => {
    const wanted = new Set(activeRoles.flatMap((role) => mapping[role] || []));
    const resolved = new Map();
    for (const key of wanted) {
      const d = destByKey.get(key);
      if (!d) continue;
      if (d.columnId) { resolved.set(key, d.columnId); continue; }
      const res = await fetch(`${API_URL}/api/blends/book/columns/ensure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(d.periodId
          ? { period_id: d.periodId }
          : { name: d.name, start_date: d.startDate, end_date: d.endDate })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Could not prepare the column for ${d.label}`);
      resolved.set(key, data.column.id);
    }
    return resolved;
  }, [activeRoles, mapping, destByKey]);

  /** Every cell to write, once the destinations are real column ids. */
  const buildPayloadRows = useCallback((resolved) => {
    const byKey = new Map(materials.map((m) => [m.key, m.id]));
    const out = [];

    const push = (materialKey, role, value) => {
      const materialId = byKey.get(materialKey);
      if (!materialId || value === null) return;
      // One figure, one row per destination it was pointed at.
      for (const destKey of mapping[role] || []) {
        const columnId = resolved.get(destKey);
        if (columnId) {
          out.push({ material_id: materialId, price_column_id: columnId, cost_per_tonne: value });
        }
      }
    };

    for (const r of rows) {
      if (!r.use) continue;
      for (const role of activeRoles) {
        if (r[role] === null) continue;          // not quoted — leave it alone
        push(r.material_key, role, num(r.final[role]));
      }
    }
    const u = num(uplift) ?? 0;
    for (const x of unmatched) {
      if (!x.materialKey) continue;
      for (const role of activeRoles) {
        if (x[role] === null) continue;
        push(x.materialKey, role, Number((x[role] + u).toFixed(2)));
      }
    }
    return out;
  }, [rows, unmatched, mapping, materials, uplift, activeRoles]);

  const apply = useCallback(async () => {
    if (mappingProblem) { setError(mappingProblem); return; }
    if (!applyCount) { setError('Nothing selected to apply.'); return; }
    setApplying(true);
    setError('');
    try {
      // Create any column (and period) the chosen destinations need, then build
      // the rows against the real ids.
      const resolved = await resolveDestinations();
      const payloadRows = buildPayloadRows(resolved);
      if (!payloadRows.length) throw new Error('Nothing to write once the destinations were resolved.');
      // Teach the aliases first. Fire-and-forget: a failed alias must not cost
      // the price update, and the same spelling next month simply won't match.
      if (remember) {
        const byKey = new Map(materials.map((m) => [m.key, m.id]));
        for (const x of unmatched) {
          if (!x.materialKey || !x.printed_name) continue;
          const id = byKey.get(x.materialKey);
          if (!id) continue;
          fetch(`${API_URL}/api/blends/materials/${id}/aliases`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ alias: x.printed_name })
          }).catch(() => {});
        }
      }

      const res = await fetch(`${API_URL}/api/blends/book/costs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          effective_date: effectiveDate,
          source: 'import',
          uplift_per_tonne: num(uplift) ?? 0,
          rows: payloadRows
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save the prices');

      onDone(
        `Imported ${data.cells_changed} price change${data.cells_changed === 1 ? '' : 's'} `
        + `effective ${formatDate(effectiveDate)} (£${num(uplift) ?? 0}/T uplift applied).`);
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  }, [applyCount, buildPayloadRows, resolveDestinations, remember, unmatched, materials,
      effectiveDate, uplift, onDone, mappingProblem]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Import prices from a supplier price list"
      size="xl"
      className="blend-import"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          {stage === 'review' && (
            <div>
              <button className="btn-slideout"
                      onClick={() => { setStage('pick'); setResult(null); setRows([]); setUnmatched([]); }}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">restart_alt</span></div>
                <span className="btn-label">Another file</span>
              </button>
            </div>
          )}
          <div>
            <button className="btn-slideout" onClick={apply}
                    disabled={stage !== 'review' || applying || !applyCount || !!mappingProblem}
                    title={mappingProblem || undefined}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${applying ? ' spinning' : ''}`}>
                  {applying ? 'progress_activity' : 'check'}
                </span>
              </div>
              <span className="btn-label">
                {applying ? 'Applying…'
                  : stage === 'review' ? `Apply ${applyCount}` : 'Apply'}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => { send(e.target.files?.[0]); e.target.value = ''; }}
      />
      <ImportBody
        stage={stage}
        error={error}
        result={result}
        rows={rows}
        setRows={setRows}
        unmatched={unmatched}
        setUnmatched={setUnmatched}
        materialOptions={materialOptions}
        uplift={uplift}
        setUplift={setUplift}
        effectiveDate={effectiveDate}
        setEffectiveDate={setEffectiveDate}
        remember={remember}
        setRemember={setRemember}
        columns={columns}
        before={before}
        onPickFile={() => fileRef.current?.click()}
        onDrop={onDrop}
        applyCount={applyCount}
        activeRoles={activeRoles}
        mapping={mapping}
        setMapping={setMapping}
        destinations={destinations}
        destByKey={destByKey}
        headingFor={headingFor}
        mappingProblem={mappingProblem}
      />
    </Modal>
  );
}
